import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, API_BASE, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import CancelDialog from "@/components/plus/CancelDialog";
import { AlertCircle, Sparkles, FileText } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-CH") : "—");
const chf = (n) => `CHF ${Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 0 })}`;

export default function SubscriptionCard({ paywall, isAdmin }) {
  const { t } = useTranslation();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const load = useCallback(() => api.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error(t("sub.checkout_error"));
      setBusy(false);
    }
  };

  const toggleCancel = async (cancel, feedback) => {
    setBusy(true);
    try {
      const { data } = await api.post(cancel ? "/billing/cancel" : "/billing/resume", cancel ? feedback : undefined);
      toast.success(data?.already ? t("sub.cancel_already") : cancel ? t("sub.cancel_done") : t("sub.resumed"));
      setAsking(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("sub.action_error"));
    } finally { setBusy(false); }
  };

  const status = sub?.status;
  const active = status === "active" && sub?.subscription_end && new Date(sub.subscription_end) > new Date();
  const plans = sub?.plans;
  const statusLabel = isAdmin ? t("sub.lifetime") : active ? (sub.cancel_at_period_end ? t("sub.cancelled") : t("sub.active")) : status === "trial" ? t("sub.trial") : t("sub.inactive");

  return (
    <div className={`card-c ${paywall ? "border-2 border-red-400" : ""}`} data-testid="subscription-card">
      {paywall && (
        <div className="mb-3 flex items-start gap-2 p-2 bg-red-50 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
          <p className="text-xs text-red-700">{t("sub.paywall")}</p>
        </div>
      )}
      <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{t("sub.title")}</p>
      <div className="flex items-center justify-between mt-2">
        <div>
          <p className="font-display text-2xl" data-testid="subscription-status">{statusLabel}</p>
          {active && !isAdmin && (
            <p className="text-xs text-text-secondary mt-0.5">
              {sub.plan === "yearly" ? t("sub.yearly") : t("sub.monthly")} · {sub.cancel_at_period_end ? t("sub.access_until") : t("sub.next_charge")} {fmt(sub.subscription_end)}
            </p>
          )}
          {!active && status === "trial" && <p className="text-xs text-text-secondary mt-0.5">{t("sub.trial_until", { date: fmt(sub.trial_end) })}</p>}
        </div>
        <Sparkles className="w-6 h-6 text-secondary-c" strokeWidth={1.8} />
      </div>

      {!active && plans && !isAdmin && (
        <>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button data-testid="subscribe-monthly" disabled={busy} onClick={() => subscribe("monthly")} className="btn-secondary text-sm">{chf(plans.monthly.amount)} / {t("sub.per_month")}</button>
            <button data-testid="subscribe-yearly" disabled={busy} onClick={() => subscribe("yearly")} className="btn-primary text-sm">{chf(plans.yearly.amount)} / {t("sub.per_year")}</button>
          </div>
          <p className="text-[11px] text-text-secondary mt-2">{sub.price_locked ? `${t("sub.price_locked")} ` : ""}{t("sub.net_prices")}</p>
        </>
      )}

      {active && !isAdmin && (
        sub.cancel_at_period_end ? (
          <button data-testid="resume-subscription" disabled={busy} onClick={() => toggleCancel(false)} className="btn-primary w-full text-sm mt-4">{t("sub.resume")}</button>
        ) : (
          <button data-testid="cancel-subscription" disabled={busy} onClick={() => setAsking(true)} className="w-full text-sm mt-4 py-2.5 rounded-xl border border-red-200 text-red-600 font-semibold hover:bg-red-50 transition-colors">{t("sub.cancel_btn")}</button>
        )
      )}
      {asking && <CancelDialog busy={busy} onClose={() => setAsking(false)} onConfirm={(fb) => toggleCancel(true, fb)} />}

      {sub?.invoices?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-c" data-testid="billing-invoices">
          <p className="text-xs font-semibold text-text-secondary mb-2">{t("sub.my_invoices")}</p>
          <ul className="space-y-1.5">
            {sub.invoices.map((inv) => (
              <li key={inv.id}>
                <button
                  data-testid={`billing-invoice-${inv.id}`}
                  onClick={() => downloadFile(`${API_BASE}/billing/invoices/${inv.id}/pdf`, `facture-${inv.number}.pdf`).catch(() => toast.error(t("sub.download_error")))}
                  className="w-full flex items-center justify-between gap-2 p-2.5 rounded-xl bg-surface-muted text-sm hover:bg-[#ece8da] transition-colors"
                >
                  <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-primary-c shrink-0" /><span className="truncate">{inv.number} · {fmt(inv.paid_at)}</span></span>
                  <span className="font-semibold shrink-0">{chf(inv.amount)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
